import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, FileText, Download, CheckCircle2, RefreshCw, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const CAMPOS = [
  { k: "resumen_ejecutivo", label: "1. Resumen ejecutivo y alcance", rows: 6 },
  { k: "especificaciones", label: "2. Especificaciones técnicas de materiales", tabla: true },
  { k: "metodologia", label: "3. Metodología de producción y montaje", rows: 6 },
  { k: "plazos", label: "4. Planificación y plazos", rows: 3 },
  { k: "seguridad_normativa", label: "5. Protocolo de seguridad y normativa", rows: 4 },
  { k: "garantia", label: "6. Garantía de calidad y soporte", rows: 3 },
];

async function abrirPdf(mid) {
  try {
    const r = await api.get(`/memorias-tecnicas/${mid}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error("Error generando el PDF"); }
}

export default function MemoriaTecnicaDialog({ peticionId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [memoria, setMemoria] = useState(null);
  const [versiones, setVersiones] = useState([]);
  const [precheck, setPrecheck] = useState(null);
  const [notasExtra, setNotasExtra] = useState("");
  const [generando, setGenerando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [modoGenerar, setModoGenerar] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: mem }, { data: pc }] = await Promise.all([
          api.get(`/peticiones/${peticionId}/memoria-tecnica`),
          api.get(`/peticiones/${peticionId}/memoria-tecnica/pre-check`),
        ]);
        setMemoria(mem.actual);
        setVersiones(mem.versiones || []);
        setPrecheck(pc);
      } catch (e) { toast.error("Error cargando la memoria técnica"); }
      finally { setLoading(false); }
    })();
  }, [peticionId]);

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post(`/peticiones/${peticionId}/memoria-tecnica/generar`, { notas_extra: notasExtra });
      setMemoria(data);
      setVersiones((v) => [{ id: data.id, version: data.version, estado: data.estado, created_at: data.created_at }, ...v]);
      setModoGenerar(false);
      toast.success(`Borrador v${data.version} generado. Revísalo y apruébalo.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando la memoria (reintenta)");
    } finally { setGenerando(false); }
  };

  const setSeccion = (k, v) => setMemoria((m) => ({ ...m, secciones: { ...m.secciones, [k]: v } }));

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.patch(`/memorias-tecnicas/${memoria.id}`, {
        titulo: memoria.titulo, subtitulo: memoria.subtitulo, secciones: memoria.secciones,
      });
      setMemoria(data);
      toast.success("Borrador guardado");
      return data;
    } catch (e) { toast.error("Error guardando"); return null; }
    finally { setGuardando(false); }
  };

  const aprobar = async () => {
    if (!window.confirm("¿Aprobar esta memoria técnica? El PDF dejará de llevar la marca BORRADOR.")) return;
    const saved = await guardar();
    if (!saved) return;
    try {
      const { data } = await api.post(`/memorias-tecnicas/${memoria.id}/aprobar`);
      setMemoria(data);
      toast.success("✅ Memoria técnica aprobada");
    } catch (e) { toast.error("Error aprobando"); }
  };

  const aprobada = memoria?.estado === "aprobada";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 w-[95vw] sm:w-auto max-w-[860px] max-h-[90vh] overflow-y-auto p-4"
        data-testid="memoria-tecnica-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <FileText className="w-5 h-5 text-cyan-400" /> Memoria técnica del proyecto
            {memoria && (
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded border font-bold ${
                aprobada ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}
                data-testid="memoria-estado-badge">
                {aprobada ? `Aprobada · v${memoria.version}` : `Borrador · v${memoria.version}`}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : generando ? (
          <div className="py-12 text-center space-y-3" data-testid="memoria-generando">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-cyan-400" />
            <div className="text-sm text-zinc-300">Redactando la memoria técnica con IA...</div>
            <div className="text-xs text-zinc-500">Puede tardar entre 30 y 90 segundos. No cierres esta ventana.</div>
          </div>
        ) : (!memoria || modoGenerar) ? (
          <div className="space-y-3" data-testid="memoria-precheck">
            {precheck?.datos_faltantes?.length > 0 ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200 space-y-1">
                <div className="font-bold">⚠️ Datos que faltan (la IA escribirá "Pendiente de definir"):</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {precheck.datos_faltantes.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
                <div className="text-zinc-400 pt-1">Puedes completarlos antes en la petición, o generar igualmente.</div>
              </div>
            ) : (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs text-emerald-200">
                ✅ Datos del proyecto completos: {precheck?.n_lineas} línea(s) y {precheck?.n_componentes} componente(s).
              </div>
            )}
            <div>
              <div className="text-xs text-zinc-400 mb-1">Indicaciones adicionales para la IA (opcional)</div>
              <Textarea rows={3} value={notasExtra} onChange={(e) => setNotasExtra(e.target.value)}
                placeholder="Ej: es un evento VIP, destacar la resistencia a tacones y líquidos; el cliente valora la rapidez de desmontaje..."
                data-testid="memoria-notas-extra" className="bg-zinc-900 border-zinc-800 text-sm" />
            </div>
            <div className="flex gap-2">
              {modoGenerar && (
                <Button variant="outline" onClick={() => setModoGenerar(false)}
                  className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
              )}
              <Button onClick={generar} className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-bold"
                data-testid="memoria-generar-btn">
                ✨ Generar {memoria ? `nueva versión (v${(versiones[0]?.version || 0) + 1})` : "memoria técnica"} con IA
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="memoria-editor">
            {memoria.datos_faltantes?.length > 0 && !aprobada && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-[11px] text-amber-200">
                ⚠️ Se generó con {memoria.datos_faltantes.length} dato(s) pendiente(s) — revisa los "Pendiente de definir".
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Título</div>
                <Input value={memoria.titulo || ""} onChange={(e) => setMemoria((m) => ({ ...m, titulo: e.target.value }))}
                  data-testid="memoria-titulo" className="bg-zinc-900 border-zinc-800 text-sm" />
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Subtítulo</div>
                <Input value={memoria.subtitulo || ""} onChange={(e) => setMemoria((m) => ({ ...m, subtitulo: e.target.value }))}
                  data-testid="memoria-subtitulo" className="bg-zinc-900 border-zinc-800 text-sm" />
              </div>
            </div>
            {CAMPOS.map((c) => (
              <div key={c.k}>
                <div className="text-[10px] uppercase text-zinc-500 mb-1">{c.label}</div>
                {c.tabla ? (
                  <div className="space-y-1.5" data-testid="memoria-especificaciones">
                    {(memoria.secciones?.especificaciones || []).map((row, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-1.5">
                        <Input value={row.componente} placeholder="Componente"
                          onChange={(e) => setSeccion("especificaciones", memoria.secciones.especificaciones.map((r, j) => j === i ? { ...r, componente: e.target.value } : r))}
                          className="bg-zinc-900 border-zinc-800 text-xs h-8" />
                        <Input value={row.material} placeholder="Material"
                          onChange={(e) => setSeccion("especificaciones", memoria.secciones.especificaciones.map((r, j) => j === i ? { ...r, material: e.target.value } : r))}
                          className="bg-zinc-900 border-zinc-800 text-xs h-8" />
                        <Input value={row.propiedades} placeholder="Propiedades y beneficios"
                          onChange={(e) => setSeccion("especificaciones", memoria.secciones.especificaciones.map((r, j) => j === i ? { ...r, propiedades: e.target.value } : r))}
                          className="bg-zinc-900 border-zinc-800 text-xs h-8" />
                        <button onClick={() => setSeccion("especificaciones", memoria.secciones.especificaciones.filter((_, j) => j !== i))}
                          className="text-zinc-600 hover:text-red-400 px-1" title="Quitar fila">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button onClick={() => setSeccion("especificaciones", [...(memoria.secciones?.especificaciones || []), { componente: "", material: "", propiedades: "" }])}
                      className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Añadir fila
                    </button>
                  </div>
                ) : (
                  <Textarea rows={c.rows} value={memoria.secciones?.[c.k] || ""}
                    onChange={(e) => setSeccion(c.k, e.target.value)}
                    data-testid={`memoria-seccion-${c.k}`}
                    className="bg-zinc-900 border-zinc-800 text-xs leading-relaxed" />
                )}
              </div>
            ))}
            <div className="text-[10px] text-zinc-500">
              💡 Las líneas que empiecen por "- " se convierten en listas con viñetas en el PDF.
              Generada por {memoria.generada_por} · v{memoria.version}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              <Button variant="outline" onClick={guardar} disabled={guardando}
                data-testid="memoria-guardar-btn"
                className="bg-transparent border-zinc-700 text-zinc-200">
                {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "💾 Guardar"}
              </Button>
              <Button variant="outline" onClick={async () => { await guardar(); abrirPdf(memoria.id); }}
                data-testid="memoria-pdf-btn"
                className="bg-transparent border-zinc-700 text-zinc-200">
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
              <Button variant="outline" onClick={() => setModoGenerar(true)}
                data-testid="memoria-regenerar-btn"
                className="bg-transparent border-zinc-700 text-zinc-400"
                title="Genera una nueva versión con IA (la actual se conserva)">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerar
              </Button>
              {!aprobada ? (
                <Button onClick={aprobar} className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold"
                  data-testid="memoria-aprobar-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aprobar
                </Button>
              ) : (
                <div className="text-[10px] text-emerald-300 self-center text-center">
                  ✅ {memoria.aprobada_por}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
